import { LogBox } from 'react-native' // eslint-disable-line no-restricted-imports
import defaultConfig from '../../config.json'
import brandMap from './brandMap.json'
import debugSettings from './debugSettings.json'
import analyticsSchemas from './analyticsSchemas.json'
import reduxPersist from './ReduxPersist'
import { logger } from 'APP/Lib/Datadog'
import { RESERVED_KEYS } from './constants'
import type { AugmentedConfigType, ConfigClass, ConfigType, HostOverride } from './types'

const BASE_CONFIG = defaultConfig as ConfigType

// Declaration merging so that the typechecker knows config keys can be accessed through the proxy.
// This does not work if done on the abstract class unfortunately.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface WebConfigClass extends AugmentedConfigType {}

/**
 * The environment is always `DEFAULT_ENVIRONMENT` (see `currentEnvironment`), on web and mobile; it never changes at runtime.
 * Web only applies hostname-based `HOST_OVERRIDES`, picked once on app start, so initialization is simple and AsyncStorage is not needed.
 *
 * WHY THE CHANGE TO CLASS
 * - Testing became inconsistent and difficult. This ensures it will always be consistent
 * - Improve devEx for future ICs (previously used and implemented with prototype causing unexpected and buggy behaviour)
 */

class WebConfigClass implements ConfigClass {
  private completeInit: () => void
  private configHostOverrides: Partial<HostOverride>
  private currentEnvironment: string
  private initPromise: Promise<void>

  constructor() {
    this.configHostOverrides = BASE_CONFIG.HOST_OVERRIDES?.[window?.location?.hostname] || {}
    this.currentEnvironment = BASE_CONFIG.DEFAULT_ENVIRONMENT
    this.completeInit = () => {}
    this.initPromise = new Promise((resolve) => {
      this.completeInit = resolve
    })
    /*
     * Proxy to handle dynamic property access.
     * when accessing Config methods, return method
     * otherwise return BASE_CONFIG property based on `getConfigValue`
     */
    return new Proxy(this, {
      get: <TKey extends keyof WebConfigClass>(target: WebConfigClass, prop: TKey) => {
        if (prop in target) {
          return target[prop]
        } else {
          return target.getConfigValue(prop as keyof AugmentedConfigType)
        }
      },
    })
  }

  applyConfigSettings() {
    if (__DEV__ && !this.getConfigValue('ENABLE_LOGS')) {
      !this.getConfigValue(RESERVED_KEYS.DEBUG_SETTINGS).yellowBox && LogBox.ignoreAllLogs()
    }
  }

  init() {
    try {
      this.applyConfigSettings()
      this.completeInit()
    } catch (e) {
      logger.error('Error initializing config:', {}, e)
    }
    return Promise.resolve()
  }

  isInitialized() {
    return this.initPromise
  }

  overrideKey<TKey extends keyof ConfigType>(key: TKey, value: ConfigType[TKey]) {
    BASE_CONFIG[key] = value
  }

  onConfigKeyChange() {
    return () => undefined
  }

  getConfigValue<TKey extends keyof AugmentedConfigType>(
    key: TKey,
    environment?: string
  ): AugmentedConfigType[TKey]
  getConfigValue(
    key: keyof AugmentedConfigType,
    environment: string = this.currentEnvironment
  ): any {
    switch (key) {
      case RESERVED_KEYS.ENVIRONMENT:
        return environment
      case RESERVED_KEYS.BRAND_MAP:
        return brandMap
      case RESERVED_KEYS.DEBUG_SETTINGS:
        return debugSettings
      case RESERVED_KEYS.ANALYTICS_SCHEMAS:
        return analyticsSchemas
      case RESERVED_KEYS.REDUX_PERSIST:
        return reduxPersist
      default: {
        // cascade the power to override the config in order of priority
        // from lowest to highest:
        // base < host
        let value: unknown = BASE_CONFIG[key as keyof ConfigType]

        if (key in this.configHostOverrides) {
          value = this.configHostOverrides[key as keyof HostOverride]
        }

        return value
      }
    }
  }
}

const Config = new WebConfigClass()

// Expose MA_CONFIG for ephemeral deployments in dev only
if (typeof window !== 'undefined' && !Config.PROD) {
  const MA_CONFIG = {
    useEphemeralMultipass: (prNumber: number) => {
      const ephemeralUrl = `https://multipass-pr-${prNumber}.dev.dialoguecorp.com`
      Config.overrideKey('MULTIPASS_DOMAIN', ephemeralUrl)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Config.MULTIPASS_PR = prNumber
      logger.info(
        `App will now use an ephemeral multipass deployment for the following PR until reloaded: https://github.com/dialoguemd/multipass/pull/${prNumber}`
      )
    },
    useEphemeralSilkroad: (prNumber: number) => {
      const ephemeralUrl = `https://silkroad-pr-${prNumber}.dev.dialoguecorp.com`
      Config.overrideKey('SILKROAD_DOMAIN', ephemeralUrl)
      logger.info(
        `App will now use an ephemeral Silkroad deployment for the following PR until reloaded: https://github.com/dialoguemd/silkroad/pull/${prNumber}`
      )
    },
    // Coredata's config key is named EMERGENCY_ROOM_DOMAIN for historical reasons
    useEphemeralCoredata: (prNumber: number) => {
      const ephemeralUrl = `https://coredata-pr-${prNumber}.dev.dialoguecorp.com`
      Config.overrideKey('EMERGENCY_ROOM_DOMAIN', ephemeralUrl)
      logger.info(
        `App will now use an ephemeral coredata deployment for the following PR until reloaded: https://github.com/dialoguemd/coredata/pull/${prNumber}`
      )
    },
  }
  window.MA_CONFIG = MA_CONFIG
}

export default Config
